import { create } from 'zustand';
import { AuthResponse, User } from '../types/api';
import { refreshSession } from '../services/api';

interface AuthState {
  accessToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  /**
   * false tant que la tentative de restauration de session (hydrate) n'est
   * pas terminée — succès ou échec. Tant que c'est false, aucune décision de
   * garde de route ne doit être prise (voir ProtectedRoute, bug 1.3/1.4).
   */
  hasHydrated: boolean;
  setSession: (payload: AuthResponse) => void;
  setAccessToken: (token: string) => void;
  setUser: (user: User) => void;
  logout: () => void;
  /**
   * Tente de restaurer la session au chargement de l'app via le cookie
   * httpOnly (POST /auth/refresh, withCredentials) — jamais via localStorage
   * (bug 1.4 : plus aucun token n'y est persisté côté web).
   */
  hydrate: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  user: null,
  isAuthenticated: false,
  hasHydrated: false,

  setSession: (payload) => {
    set({ accessToken: payload.accessToken, user: payload.user, isAuthenticated: true });
  },

  setAccessToken: (token) => {
    set({ accessToken: token, isAuthenticated: true });
  },

  setUser: (user) => {
    set({ user });
  },

  logout: () => {
    set({ accessToken: null, user: null, isAuthenticated: false });
  },

  hydrate: async () => {
    // Passe par le point d'entrée unique de refresh (services/api.ts), partagé
    // avec l'intercepteur 401 — le refresh token étant à usage unique côté
    // backend (rotation + détection de réutilisation), un appel indépendant ici
    // pouvait entrer en course avec un refresh déclenché ailleurs (double montage
    // React.StrictMode, focus de fenêtre, plusieurs onglets) et casser la
    // session en silence. `refreshSession()` positionne déjà accessToken/user/
    // isAuthenticated via setSession()/logout() — il ne reste qu'à marquer la
    // restauration terminée.
    await refreshSession();
    set({ hasHydrated: true });
  },
}));
